import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminLogin as apiAdminLogin, AdminUser } from '../api/adminAuth';
import { getAdminProfile } from '../api/adminAuth';

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  isAdminAuthenticated: boolean;
  isAdminLoading: boolean;
  adminLogin: (email: string, password: string) => Promise<void>;
  adminLogout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isAdminLoading, setIsAdminLoading] = useState(true);

  useEffect(() => {
    const loadAdminUser = async () => {
      try {
        const token = localStorage.getItem('admin_access_token');
        if (token) {
          const profile = await getAdminProfile(token);
          if (profile.is_super_admin) {
            setAdminUser({
              id: profile.id,
              email: profile.email,
              full_name: profile.fullName,
              is_super_admin: true,
            });
          } else {
            throw new Error('Not a super admin');
          }
        }
      } catch {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
      } finally {
        setIsAdminLoading(false);
      }
    };

    loadAdminUser();
  }, []);

  const adminLogin = async (email: string, password: string) => {
    const response = await apiAdminLogin(email, password);

    localStorage.setItem('admin_access_token', response.accessToken);
    localStorage.setItem('admin_refresh_token', response.refreshToken);
    localStorage.setItem('admin_user', JSON.stringify(response.user));

    setAdminUser(response.user);
  };

  const adminLogout = () => {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    setAdminUser(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        adminUser,
        isAdminAuthenticated: !!adminUser,
        isAdminLoading,
        adminLogin,
        adminLogout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
